import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IntegratedInitialAssessmentForm from "./IntegratedAssessmentForm";
import { updateIntegratedAssessment, fetchIntegratedAssessment } from "../../store/slices/integratedAssessmentSlice";
import { getApiErrorMessage } from "../../utils/apiError";

/* =========================
   NORMALIZATION HELPERS
========================= */
const toArray = (value) =>
    Array.isArray(value)
        ? value
        : typeof value === "string" && value.length
            ? value.split(",").map(v => v.trim())
            : [];

const toString = (value) =>
    Array.isArray(value) ? value.join(", ") : value || "";

const toBool = (value) =>
    value === 1 || value === "1" || value === true || value === "Yes";

const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
};

/* =========================
   COMPONENT
========================= */
const EditIntegratedInitialAssessment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const [formData, setFormData] = useState(null);
    const [savingSection, setSavingSection] = useState(null);

    /* =========================
       MAP API → FORM STATE
    ========================= */
    useEffect(() => {
        const loadAssessment = async () => {
            try {
                const response = await dispatch(fetchIntegratedAssessment(id)).unwrap();
                const data = response; // or response.data depending on your API response structure

                setFormData({
                    /* ---------- BASIC ---------- */
                    patientId: data.patient_id,
                    caseId: data.case_id,
                    // New one-to-one sections (nested objects)
                    cognitive_assessment: data.cognitive_assessment || {},
                    discharge_planning: data.discharge_planning || {},
                    mrNumber: data.mr_number || "",
                    date: data.date || "",
                    time: data.time?.substring(0, 5) || "",
                    name: data.name || "",
                    age: data.age || "",
                    occupation: data.occupation || "",
                    gender: data.gender || "",
                    maritalStatus: data.marital_status || "",
                    sourceOfReferral: data.source_of_referral || "",
                    informant: data.informant || "",
                    comorbid: data.comorbid || "",
                    consentType: data.consent_type || "",
                    consentReason: data.consent_reason || "",
                    presentingComplaints: data.presenting_complaints || "",
                    psychoSocialStressors: data.psycho_social_stressors || "",
                    functionalAdjustment: data.functional_adjustment || "",
                    vegetativeFunctions: data.vegetative_functions || "",
                    currentTreatment: data.current_treatment || "",
                    allergies: data.allergies || "",
                    duration: data.duration || "",
                    episodes: data.episodes || "",
                    ect: data.ect,
                    course: data.course || "",
                    compliance: data.compliance || "",
                    admission: data.admission || "",
                    opdFU: data.opd_fu || "",
                    lai: data.lai || "",
                    epsSideEffects: data.eps_side_effects || "",
                    roh: data.roh || "",
                    ros: data.ros || "",
                    rov: data.rov || "",
                    roe: data.roe || "",
                    rof: data.rof || "",
                    rovu: data.rovu || "",
                    suicideFormNursing: data.risk_of_suicide || "",
                    violenceFormNursing: data.risk_of_violence || "",
                    safetyCarePlanNursing: data.time_of_discharge || "",

                    /* ---------- PART ONE ---------- */
                    medicalSurgicalHistory: data.part_one?.medical_surgical_history || "",
                    personalHistory: data.part_one?.personal_history || "",
                    educationalHistory: data.part_one?.educational_history || "",
                    occupationalHistory: data.part_one?.occupational_history || "",
                    spiritualHistory: data.part_one?.spiritual_history || "",
                    maritalHistory: data.part_one?.marital_history || "",
                    legalIssues: data.part_one?.legal_issues || "",
                    preMorbidPersonality: data.part_one?.pre_morbid_personality || "",
                    substanceUse: data.part_one?.substance_use || "",
                    familyHistory: data.part_one?.family_history || "",
                    familyOrigin: toArray(data.part_one?.family_origin),
                    familyPsychIllness: toBool(data.part_one?.family_psycho_ill),
                    generalAppearance: data.part_one?.general_appearance || "",
                    cranialNerve: data.part_one?.cranial_nerve || "",
                    sensorySystem: data.part_one?.sensory_system || "",
                    motorSystem: data.part_one?.motor_system || "",
                    meningealIrritation: data.part_one?.meningeal_irritation || "",
                    cerebellarSigns: data.part_one?.cerebellar_signs || "",
                    pallor: data.part_one?.pallor || "yes",
                    jaundice: data.part_one?.jaundice || "",
                    cyanosis: data.part_one?.cyanosis || "",
                    cutMarks: data.part_one?.cut_marks || "",
                    clubing: data.part_one?.clubing || "",
                    thyroid: data.part_one?.thyroid || "",
                    lymphNodes: data.part_one?.lymph_nodes || "",
                    burnMarks: data.part_one?.burn_marks || "",
                    dehydration: data.part_one?.dehydration || "",
                    edema: data.part_one?.edema || "",
                    abnormalMovementOrTremors: data.part_one?.abnormal_movement_or_tremors || "",
                    bulkRightUpper: data.part_one?.bulk_right_upper || "",
                    bulkRightLower: data.part_one?.bulk_right_lower || "",
                    bulkLeftUpper: data.part_one?.bulk_left_upper || "",
                    bulkLeftLower: data.part_one?.bulk_left_lower || "",
                    toneRightUpper: data.part_one?.tone_right_upper || "",
                    toneRightLower: data.part_one?.tone_right_lower || "",
                    toneLeftUpper: data.part_one?.tone_left_upper || "",
                    toneLeftLower: data.part_one?.tone_left_lower || "",
                    powerRightUpper: data.part_one?.power_right_upper || "",
                    powerRightLower: data.part_one?.power_right_lower || "",
                    powerLeftUpper: data.part_one?.power_left_upper || "",
                    powerLeftLower: data.part_one?.power_left_lower || "",
                    reflexesRightUpper: data.part_one?.reflexes_right_upper || "",
                    reflexesRightLower: data.part_one?.reflexes_right_lower || "",
                    reflexesLeftUpper: data.part_one?.reflexes_left_upper || "",
                    reflexesLeftLower: data.part_one?.reflexes_left_lower || "",

                    /* ---------- PART TWO ---------- */
                    respiratoryInspection: data.part_two?.respiratory_inspection || "",
                    respiratoryPalpation: data.part_two?.respiratory_palpation || "",
                    respiratoryPercussion: data.part_two?.respiratory_percussion || "",
                    respiratoryAuscultation: data.part_two?.respiratory_auscultation || "",
                    abdominalInspection: data.part_two?.abdominal_inspection || "",
                    abdominalPalpation: data.part_two?.abdominal_palpation || "",
                    abdominalPercussion: data.part_two?.abdominal_percussion || "",
                    abdominalAuscultation: data.part_two?.abdominal_auscultation || "",
                    cardioInspection: data.part_two?.cardio_inspection || "",
                    cardioPalpation: data.part_two?.cardio_palpation || "",
                    cardioPercussion: data.part_two?.cardio_percussion || "",
                    cardioAuscultation: data.part_two?.cardio_auscultation || "",
                    akathisia: data.part_two?.akathisia || "",
                    dystonia: data.part_two?.dystonia || "",
                    td: data.part_two?.td || "",
                    parkinsons: data.part_two?.parkinsons || "",
                    provisionalDiagnosis: data.part_two?.provisional_diagnosis || "",
                    differentialDiagnosis: data.part_two?.differential_diagnosis || "",
                    diagnosisTreatmentDetails: data.part_two?.diagnosis_treatment_details || "",
                    consentTakenClient: toBool(data.part_two?.consent_taken_client),
                    consentTakenFamily: toBool(data.part_two?.consent_taken_family),
                    consentTakenGuardian: toBool(data.part_two?.consent_taken_guardian),
                    consentTakenTerm: data.part_two?.consent_taken_term || "",
                    typeOfAdmission: data.part_two?.type_of_admission || "Elective",
                    unit: data.part_two?.unit || "",
                    dateOfAdmission: data.part_two?.date_of_admission || "",
                    timeOfAdmission: data.part_two?.time_of_admission || "",
                    modeOfTransportation: data.part_two?.mode_of_transportation || "",
                    approxLengthOfStay: data.part_two?.approx_length_of_stay || "",
                    diagnosticWorkup: data.part_two?.diagnostic_workup || "",
                    pharmacotherapy: data.part_two?.pharmacotherapy || "",
                    treatmentOthers: data.part_two?.treatment_others || "",
                    consultants: data.part_two?.consultants || "",
                    residentName: data.part_two?.resident_name || "",
                    cellNumber: data.part_two?.resident_cell_number || "",
                    residentSignDate: data.part_two?.resident_sign_date || "",
                    residentTime: data.part_two?.resident_time || "",
                    doctorsName: data.part_two?.consultant_doctors_name || "",
                    consultantSignDate: data.part_two?.consultant_sign_date || "",
                    consultantTime: data.part_two?.consultant_time || "",
                    accompaniedBy: data.part_two?.accompanied_by || "",
                    idBand: data.part_two?.id_band || "",
                    nursingAge: data.part_two?.nursing_age || "",
                    nursingWeight: data.part_two?.weight || "",
                    nursingHeight: data.part_two?.height || "",
                    bloodPressure: data.part_two?.blood_pressure || "",
                    temperature: data.part_two?.temperature || "",
                    pulse: data.part_two?.pulse || "",
                    respRate: data.part_two?.resp_rate || "",
                    o2Saturation: data.part_two?.o2_saturation || "",
                    reasonForAdmission: data.part_two?.reason_for_admission || "",
                    communicableDisease: toArray(data.part_two?.communicable_disease),
                    communicable_None: data.part_two?.communicable_none || "",
                    communicable_Others: data.part_two?.communicable_other || "",
                    isolationType: data.part_two?.isolation_type || "",
                    isolationOther: data.part_two?.isolation_other || "",
                    eyeGlasses: data.part_two?.eye_glasses || "",
                    hearingAids: data.part_two?.hearing_aids || "",
                    dentures: data.part_two?.dentures || "",
                    jewelry: data.part_two?.jewelry || "",
                    personalOthers: data.part_two?.others || "",

                    /* ---------- PART THREE ---------- */
                    mdrOrganisms: toArray(data.part_three?.mdr_organisms),
                    informedValuablePolicy: data.part_three?.informed_valuable_policy || "",
                    presenceOfPain: data.part_three?.presence_of_pain || "",
                    painScore: toNumber(data.part_three?.pain_score),
                    painScaleUsed: toArray(data.part_three?.pain_scale_used),
                    painLocation: data.part_three?.pain_location || "",

                    /* ---------- PART FOUR ---------- */
                    levelOfConscious: data.part_four?.level_of_conscious || "Alert",
                    sensory: data.part_four?.sensory || "Asymptomatic",
                    sensoryOthers: data.part_four?.sensory_others || "",
                    vision: data.part_four?.vision || "Normal",
                    visionOthers: data.part_four?.vision_others || "",
                    hearing: data.part_four?.hearing || "Normal",
                    hearingOthers: data.part_four?.hearing_others || "",
                    speech: data.part_four?.speech || "Normal",
                    speechOthers: data.part_four?.speech_others || "",
                    selfCaring: toArray(data.part_four?.self_caring),
                    selfCaringOther: data.part_four?.self_caring_other || "",
                    musculoskeletal: toArray(data.part_four?.musculoskeletal),
                    musculoskeletalOther: data.part_four?.musculoskeletal_other || "",
                    assisting: toArray(data.part_four?.assisting),
                    assistingOther: data.part_four?.assisting_other || "",
                    admissionItems: toArray(data.part_four?.admission_items),
                    cognition: toArray(data.part_four?.cognition) || "",
                    clientLevel: data.part_four?.client_level || "",
                    clientLevelOthers: data.part_four?.client_level_others || "",
                    roomAllocation: data.part_four?.room_allocation || "",
                    informMedicine: data.part_four?.inform_medicine || "",
                    guideFamilyMeetings: data.part_four?.guide_family_meetings || "",
                    infoConsultants: data.part_four?.info_consultants || "",
                    authorizeHairCutting: data.part_four?.authorize_hair_cutting || "",
                    isolationContagious: data.part_four?.isolation_contagious || "",
                    weightKg: data.part_four?.weight_kg || "0",
                    heightCm: data.part_four?.height_cm || "0",
                    bmi: data.part_four?.bmi || "0",
                    bmiLess18: data.part_four?.bmi_less_18 || "",
                    bmiGreater25: data.part_four?.bmi_greater_25 || "",
                    lostWeight3Months: data.part_four?.lost_weight_3_months || "",
                    recentHospitalization: data.part_four?.recent_hospitalization || "",
                    dietitianReferralConditions: toArray(data.part_four?.dietitian_referral_conditions) || "",
                    referDietitian: data.part_four?.refer_dietitian || "",
                    rohNursing: data.part_four?.roh_nursing || "",
                    rosNursing: data.part_four?.ros_nursing || "",
                    rovNursing: data.part_four?.rov_nursing || "",
                    roeNursing: data.part_four?.roe_nursing || "",
                    rofNursing: data.part_four?.rof_nursing || "",
                    rovuNursing: data.part_four?.rovu_nursing || "",
                    suicideFormNursing2: data.part_four?.suicide_form_nursing || "",
                    violenceFormNursing2: data.part_four?.violence_form_nursing || "",
                    safetyCarePlanNursing2: data.part_four?.safety_care_plan_nursing || "",
                    appearance: data.part_four?.appearance || "",
                    behavior: data.part_four?.behavior || "",
                    speechMse: data.part_four?.speech_mse || "",
                    moodAffect: data.part_four?.mood_affect || "",
                    thoughtProcess: data.part_four?.thought_process || "",
                    thoughtContent: data.part_four?.thought_content || "",
                    insight: data.part_four?.insight || "Partial",
                    memory: data.part_four?.memory || "Intact",
                    selfEsteem: data.part_four?.self_esteem || "",
                    hopelessness: data.part_four?.hopelessness || "",
                    worthlessness: data.part_four?.worthlessness || "",
                    coping: data.part_four?.coping || "",
                    maritalStatusSocio: data.part_four?.marital_status_socio || "",
                    noOfChildren: data.part_four?.no_of_children || "",
                    workSituation: data.part_four?.work_situation || "",
                    occupationSocio: data.part_four?.occupation || "",
                    habit: data.part_four?.habit || "",
                    habitSpecify: data.part_four?.habit_specify || "",
                    livingSituation: data.part_four?.living_situation || "",
                    livingSituationSpecify: data.part_four?.living_situation_specify || "",

                    /* ---------- PART FIVE ---------- */
                    religion: data.part_five?.religion || "",
                    religionOther: data.part_five?.religion_other || "",
                    religiousPractices: data.part_five?.religious_practices || "No",
                    privacy: data.part_five?.privacy || "Standard",
                    food: data.part_five?.food || "Normal",
                    genderRegalia: data.part_five?.gender_regalia || "Appropriate",
                    otherValues: data.part_five?.other_values || "",
                    mouthTongue: data.part_five?.mouth_tongue || "Normal",
                    oralSignSymptom: data.part_five?.oral_sign_symptom || "Asymptomatic",
                    oralSignSymptomOthers: data.part_five?.oral_sign_symptom_others || "",
                    ngGastric: data.part_five?.ng_gastric || "No",
                    ngGastricDate: data.part_five?.ng_gastric_date || "",
                    skinAppearance: data.part_five?.skin_appearance || "Normal",
                    skinAppearanceOther: data.part_five?.skin_appearance_other || "",
                    skinTemperature: data.part_five?.skin_temperature || "Normal",
                    skinContagious: data.part_five?.skin_contagious || "Non Contagious",
                    skinLocation: data.part_five?.skin_location || "None",
                    skinReasons: data.part_five?.skin_reasons || "",
                    cardioPulse: toNumber(data.part_five?.cardio_pulse),
                    cardioSignSymptom: data.part_five?.cardio_sign_symptom || "Asymptomatic",
                    cardioSignSymptomOther: data.part_five?.cardio_sign_symptom_other || "",
                    respiration: data.part_five?.respiration || "Regular",
                    respirationOther: data.part_five?.respiration_other || "",
                    cough: data.part_five?.cough || "None",
                    coughOther: data.part_five?.cough_other || "",
                    bowelMovement: data.part_five?.bowel_movement || "Normal",
                    bowelSignSymptom: data.part_five?.bowel_sign_symptom || "Asymptomatic",
                    urinaryAppearance: data.part_five?.urinary_appearance || "Clear",
                    urinarySignSymptom: data.part_five?.urinary_sign_symptom || "Asymptomatic",
                    dateOfInsertion: data.part_five?.date_of_insertion || "",
                    catheterType: data.part_five?.catheter_type || "None",
                    urinaryOthers: data.part_five?.urinary_others || "",
                    obsGyn: data.part_five?.obs_gyn || "Not Applicable",
                    menstrualCycle: data.part_five?.menstrual_cycle || "Not Applicable",
                    dysmenorrhea: data.part_five?.dysmenorrhea || "",
                    skinChanges: data.part_five?.skin_changes || "",
                    obsGynOthers: data.part_five?.obs_gyn_others || "",
                    nursingStaff: data.part_five?.nursing_staff || "",
                    checkedBy: data.part_five?.checked_by || "",
                    designation: data.part_five?.designation || "",
                    nursingDate: data.part_five?.nursing_date || "",
                    nursingTime: data.part_five?.nursing_time || "",
                    signature: data.part_five?.signature || "",
                    headNurse: data.part_five?.head_nurse || "",
                    clinicalNurseInstructor: data.part_five?.clinical_nurse_instructor || "",
                    teamLeader: data.part_five?.team_leader || "",
                    irpName: data.part_five?.irp_name || "",
                    irpDate: data.part_five?.irp_date || "",
                    domain: data.part_five?.irp_domain || "",
                    rehabGoal: data.part_five?.irp_rehab_goal || "",
                    strengths: data.part_five?.irp_strengths || "",
                    skillsNeeded: data.part_five?.irp_skills_needed || "",
                    clientSignature: data.part_five?.irp_client_signature || "",
                    clientDate: data.part_five?.irp_client_date || "",
                    practitionerSignature: data.part_five?.irp_practitioner_signature || "",
                    practitionerDate: data.part_five?.irp_practitioner_date || "",
                    endorsement: data.part_five?.endorsement || "",

                    /* ---------- PART SIX ---------- */
                    discharge: data.part_six?.discharge || "Planned",
                    dischargeInstructionTo: data.part_six?.discharge_instruction_to || "Family",
                    specialInstructions: data.part_six?.special_instructions || "None",
                    injections: data.part_six?.injections || "None",
                    anyRedFlag: data.part_six?.any_red_flag || "None",
                    riskAssessmentDC: data.part_six?.risk_assessment_dc || "Low",
                    expectedDischargeDate: data.part_six?.expected_discharge_date || "",
                    homeCare: data.part_six?.home_care || "Family Member",
                    homeCareOthersSpecify: data.part_six?.home_care_others_specify || "N/A",
                    transportation: data.part_six?.transportation || "No",
                    psychotherapy: data.part_six?.psychotherapy || "No",
                    dayRehab: data.part_six?.day_rehab || "No",
                    dayRehabDays: data.part_six?.day_rehab_days || "0",
                    longTermCare: data.part_six?.long_term_care || "No",
                    otherNeeds: data.part_six?.other_needs || "No",
                    otherNeedsSpecify: data.part_six?.other_needs_specify || "N/A",
                    educateIllness: data.part_six?.educate_illness || "",
                    educateHygiene: data.part_six?.educate_hygiene || "",
                    educateMedication: data.part_six?.educate_medication || "",
                    unusualBehavior: data.part_six?.unusual_behavior || "",
                    givingMedicine: data.part_six?.giving_medicine || "",
                    contactDoctor: data.part_six?.contact_doctor || "",
                    clientSharing: data.part_six?.client_sharing || "",
                    engagedActivities: data.part_six?.engaged_activities || "",
                    triggerRelapse: data.part_six?.trigger_relapse || "",
                    sleepWake: data.part_six?.sleep_wake || "",
                    eatingMeals: data.part_six?.eating_meals || "",
                    physicalActivity: data.part_six?.physical_activity || "",
                    copingStrategies: data.part_six?.coping_strategies || "",
                    preventingRelapse: data.part_six?.preventing_relapse || "",
                    sociallyConnected: data.part_six?.socially_connected || "",
                    diet: data.part_six?.diet || "",
                    takeHomeMedication: data.part_six?.take_home_medication || "",
                    complianceTreatment: data.part_six?.compliance_treatment || "",
                    followupVisit: data.part_six?.followup_visit || "Weekly",
                    effectiveCoping: data.part_six?.effective_coping || "",
                    hospitalEmergency: data.part_six?.hospital_emergency || "",
                    activityHome: data.part_six?.activity_home || "",
                    stressManagement: data.part_six?.stress_management || "",
                    aggressionManagement: data.part_six?.aggression_management || "",
                    alarmingSigns: data.part_six?.alarming_signs || "",
                    occupationalTherapy: data.part_six?.occupational_therapy || "",
                    socialSupport: data.part_six?.social_support || "",

                    environmentalSafety: data.part_six?.environmental_safety || "",
                    processGiverRoleStrain: data.part_six?.process_giver_role_strain || "",
                    longTermFacilities: data.part_six?.long_term_facilities || "",
                    contactNumber: data.part_six?.contact_number || "",
                    others: data.part_six?.others || "",
                    nameSignature: data.part_six?.name_signature || "",
                    signatureDate: data.part_six?.signature_date || "",
                    signatureTime: data.part_six?.signature_time || "",

                    possibleRelapseSign: data.part_six?.possible_relapse_sign || "",
                    recoveryGoals: data.part_six?.recovery_goals || "",
                    whoToContact: data.part_six?.who_to_contact || "",
                    whereToGoCrises: data.part_six?.where_to_go_crises || "",
                    detailsMedication: data.part_six?.details_medication || "",
                    detailTreatmentPlan: data.part_six?.detail_treatment_plan || "",
                    physicalHealthNeeds: data.part_six?.physical_health_needs || "",
                    dateOfReview: data.part_six?.date_of_review || "",
                    admissionPathway: data.part_six?.admission_pathway || "",
                    riskAssessmentSafetyPlan: data.part_six?.risk_assessment_safety_plan || "",
                    doctorName: data.part_six?.doctor_name || "",
                    guardianSignature: data.part_six?.guardian_signature || "Signed",
                    guardianName: data.part_six?.guardian_name || "",
                    nameOfDocuments: data.part_six?.name_of_documents || "",
                    effectiveDate: data.part_six?.effective_date || "",
                    approvedBy: data.part_six?.approved_by || "",
                    approvedDate: data.part_six?.approved_date || "",
                    nextRevisionBy: data.part_six?.next_revision_by || "",
                    nextRevisionDate: data.part_six?.next_revision_date || "",

                    /* ---------- PROGRESS NOTES ---------- */
                    // progressNote: data.monthly_pro_notes?.progress_note || "Initial Assessment",
                    familyMeetingMinutes: data.monthly_pro_notes?.family_meeting_minutes || "Scheduled",
                    discussionSummary: data.monthly_pro_notes?.discussion_summary || "N/A",

                    /* ---------- ARRAYS - Map API format to form format ---------- */
                    nursingProblems: (data.nursing_needs || []).map((item, idx) => ({
                        id: item.id || idx,
                        problem: item.problem || item.nursing_need || ""
                    })),
                    generalProblems: (data.problem_list || []).map((item, idx) => ({
                        id: item.id || idx,
                        problem: item.problem || "" // Ensure this key matches what the child component input expects (e.g. value={item.problem})
                    })),
                    objectives: (data.obj_act_plan || []).map((item, idx) => ({
                        id: item.id || idx,
                        name: item.objective || "",
                        obj_status: item.action_status || "Pending",
                        obj_date: item.date || "",
                        actions: (item.step || []).map((step, sIdx) => ({
                            id: step.id || sIdx,
                            action_name: step.action_step || "",  // Match Add component field name
                            location: step.location || "",
                            frequency: step.frequency || "",
                            duration: step.duration || ""
                        }))
                    })),
                    /* ---------- PROGRESS NOTES ---------- */
                    // We must wrap the single API object into an array to match the UI structure
                    monthlyProgress: Array.isArray(data.monthly_pro_notes)
                        ? data.monthly_pro_notes.map(item => ({
                            id: item.id,
                            // API 'note_date' -> Form 'progressDate'
                            progressDate: item.note_date || "",
                            // API 'date' -> Form 'meetingDate'
                            meetingDate: item.date || "",
                            // API 'progress_note' -> Form 'update' (Crucial mapping)
                            update: item.progress_note || "",
                            familyMeetingMinutes: item.family_meeting_minutes || "",
                            discussionSummary: item.discussion_summary || ""
                        }))
                        : [],

                    // Keep API format for submission
                    nursing_needs: data.nursing_needs || [],
                    problem_list: data.problem_list || [],
                    obj_act_plan: data.obj_act_plan || [],
                });
            } catch (err) {
                console.error("Failed to load assessment:", err);
                Swal.fire("Error", getApiErrorMessage(err, "Failed to fetch assessment data"), "error");
            }
        };

        loadAssessment();
    }, [id, dispatch]);

    /* =========================
       CHANGE HANDLER
    ========================= */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle checkbox arrays (like familyOrigin)
        if (type === "checkbox" && name === "familyOrigin") {
            setFormData(prev => ({
                ...prev,
                [name]: checked
                    ? [...(prev[name] || []), value]
                    : (prev[name] || []).filter(item => item !== value),
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value
            }));
        }
    };

    /* =========================
       SECTION SAVE HANDLER
    ========================= */
    const handleSectionSave = async (section) => {
        setSavingSection(section);
        try {
            const payload = buildCompletePayload();
            const result = await dispatch(updateIntegratedAssessment({ id, payload })).unwrap();

            // Refresh form data with updated values - convert API format to form format
            if (result && result.id) {
                setFormData(prev => ({
                    ...prev,
                    // Convert API arrays back to form format
                    nursingProblems: (result.nursing_needs || []).map((item, idx) => ({
                        id: item.id || idx,
                        problem: item.problem || item.nursing_need || ""
                    })),
                    supportingData: (result.problem_list || []).map((item, idx) => ({
                        id: item.id || idx,
                        data: item.problem || item.data || ""
                    })),
                    objectives: (result.obj_act_plan || []).map((item, idx) => ({
                        id: item.id || idx,
                        name: item.objective || "",
                        obj_status: item.action_status || "Pending",
                        obj_date: item.date || new Date().toISOString().split('T')[0],
                        actions: (item.step || []).map((step, sIdx) => ({
                            id: step.id || sIdx,
                            action_name: step.action_step || "",
                            location: step.location || "",
                            frequency: step.frequency || "",
                            duration: step.duration || ""
                        }))
                    })),
                }));
            }

            Swal.fire("Updated!", `${section} updated successfully.`, "success");
        } catch (err) {
            console.error("Section save error:", err);
            Swal.fire("Error!", `Failed to update ${section}. ${getApiErrorMessage(err, '')}`, "error");
        } finally {
            setSavingSection(null);
        }
    };

    /* =========================
       BUILD COMPLETE PAYLOAD
    ========================= */
    const buildCompletePayload = () => {
        return {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            mr_number: formData.mrNumber,
            date: formData.date,
            time: formData.time,
            name: formData.name,
            age: toNumber(formData.age),
            occupation: formData.occupation,
            gender: formData.gender,
            marital_status: formData.maritalStatus,
            source_of_referral: formData.sourceOfReferral,
            informant: formData.informant,
            comorbid: formData.comorbid,
            consent_type: formData.consentType,
            consent_reason: formData.consentReason,
            presenting_complaints: formData.presentingComplaints,
            psycho_social_stressors: formData.psychoSocialStressors,
            functional_adjustment: formData.functionalAdjustment,
            vegetative_functions: formData.vegetativeFunctions,
            current_treatment: formData.currentTreatment,
            allergies: formData.allergies,
            duration: formData.duration,
            episodes: formData.episodes,
            ect: formData.ect,
            course: formData.course,
            compliance: formData.compliance,
            admission: formData.admission,
            opd_fu: formData.opdFU,
            lai: formData.lai,
            eps_side_effects: formData.epsSideEffects,
            roh: formData.roh,
            ros: formData.ros,
            rov: formData.rov,
            roe: formData.roe,
            rof: formData.rof,
            rovu: formData.rovu,
            risk_of_suicide: formData.suicideFormNursing,
            risk_of_violence: formData.violenceFormNursing,
            time_of_discharge: formData.safetyCarePlanNursing,

            part_one: {
                medical_surgical_history: formData.medicalSurgicalHistory,
                personal_history: formData.personalHistory,
                educational_history: formData.educationalHistory,
                occupational_history: formData.occupationalHistory,
                spiritual_history: formData.spiritualHistory,
                marital_history: formData.maritalHistory,
                legal_issues: formData.legalIssues,
                pre_morbid_personality: formData.preMorbidPersonality,
                substance_use: formData.substanceUse,
                family_history: formData.familyHistory,
                family_origin: Array.isArray(formData.familyOrigin)
                    ? formData.familyOrigin.join(', ')
                    : formData.familyOrigin || "",
                family_psycho_ill: formData.familyPsychIllness ? 1 : 0,
                general_appearance: formData.generalAppearance,
                cranial_nerve: formData.cranialNerve,
                sensory_system: formData.sensorySystem,
                motor_system: formData.motorSystem,
                meningeal_irritation: formData.meningealIrritation,
                cerebellar_signs: formData.cerebellarSigns,
                pallor: formData.pallor,
                jaundice: formData.jaundice,
                cyanosis: formData.cyanosis,
                cut_marks: formData.cutMarks,
                clubing: formData.clubing,
                thyroid: formData.thyroid,
                lymph_nodes: formData.lymphNodes,
                burn_marks: formData.burnMarks,
                dehydration: formData.dehydration,
                edema: formData.edema,
                abnormal_movement_or_tremors: formData.abnormalMovementOrTremors,
                bulk_right_upper: formData.bulkRightUpper,
                bulk_right_lower: formData.bulkRightLower,
                bulk_left_upper: formData.bulkLeftUpper,
                bulk_left_lower: formData.bulkLeftLower,
                tone_right_upper: formData.toneRightUpper,
                tone_right_lower: formData.toneRightLower,
                tone_left_upper: formData.toneLeftUpper,
                tone_left_lower: formData.toneLeftLower,
                power_right_upper: formData.powerRightUpper,
                power_right_lower: formData.powerRightLower,
                power_left_upper: formData.powerLeftUpper,
                power_left_lower: formData.powerLeftLower,
                reflexes_right_upper: formData.reflexesRightUpper,
                reflexes_right_lower: formData.reflexesRightLower,
                reflexes_left_upper: formData.reflexesLeftUpper,
                reflexes_left_lower: formData.reflexesLeftLower,
            },

            part_two: {
                respiratory_inspection: formData.respiratoryInspection,
                respiratory_palpation: formData.respiratoryPalpation,
                respiratory_percussion: formData.respiratoryPercussion,
                respiratory_auscultation: formData.respiratoryAuscultation,
                abdominal_inspection: formData.abdominalInspection,
                abdominal_palpation: formData.abdominalPalpation,
                abdominal_percussion: formData.abdominalPercussion,
                abdominal_auscultation: formData.abdominalAuscultation,
                cardio_inspection: formData.cardioInspection,
                cardio_palpation: formData.cardioPalpation,
                cardio_percussion: formData.cardioPercussion,
                cardio_auscultation: formData.cardioAuscultation,
                akathisia: formData.akathisia,
                dystonia: formData.dystonia,
                td: formData.td,
                parkinsons: formData.parkinsons,
                provisional_diagnosis: formData.provisionalDiagnosis,
                differential_diagnosis: formData.differentialDiagnosis,
                diagnosis_treatment_details: formData.diagnosisTreatmentDetails,
                consent_taken_client: formData.consentTakenClient ? 1 : 0,
                consent_taken_family: formData.consentTakenFamily ? 1 : 0,
                consent_taken_guardian: formData.consentTakenGuardian ? 1 : 0,
                consent_taken_term: formData.consentTakenTerm,
                approx_length_of_stay: formData.approxLengthOfStay,
                diagnostic_workup: formData.diagnosticWorkup,
                pharmacotherapy: formData.pharmacotherapy,
                treatment_others: formData.treatmentOthers,
                consultants: formData.consultants,
                resident_name: formData.residentName,
                resident_cell_number: formData.cellNumber,
                resident_sign_date: formData.residentSignDate,
                resident_time: formData.residentTime,
                consultant_doctors_name: formData.doctorsName,
                consultant_sign_date: formData.consultantSignDate,
                consultant_time: formData.consultantTime,
                type_of_admission: formData.typeOfAdmission,
                unit: formData.unit,
                date_of_admission: formData.dateOfAdmission,
                time_of_admission: formData.timeOfAdmission,
                mode_of_transportation: formData.modeOfTransportation,
                accompanied_by: formData.accompaniedBy,
                id_band: formData.idBand,
                nursing_age: formData.nursingAge,
                weight: parseFloat(formData.nursingWeight) || 0,
                height: parseFloat(formData.nursingHeight) || 0,
                blood_pressure: formData.bloodPressure,
                temperature: toNumber(formData.temperature),
                pulse: toNumber(formData.pulse),
                resp_rate: toNumber(formData.respRate),
                o2_saturation: toNumber(formData.o2Saturation),
                reason_for_admission: formData.reasonForAdmission,
                communicable_disease: Array.isArray(formData.communicableDisease) ? formData.communicableDisease.join(', ') : formData.communicableDisease,
                communicable_none: formData.communicable_None,
                communicable_other: formData.communicable_Others,
                isolation_type: formData.isolationType,
                isolation_other: formData.isolationOther,
                eye_glasses: formData.eyeGlasses,
                hearing_aids: formData.hearingAids,
                dentures: formData.dentures,
                jewelry: formData.jewelry,
                others: formData.personalOthers,
            },

            part_three: {
                mdr_organisms: toString(formData.mdrOrganisms),
                informed_valuable_policy: formData.informedValuablePolicy,
                presence_of_pain: formData.presenceOfPain,
                pain_score: toNumber(formData.painScore),
                pain_scale_used: toString(formData.painScaleUsed),
                pain_location: formData.painLocation,
            },

            part_four: {
                level_of_conscious: formData.levelOfConscious,
                sensory: formData.sensory,
                sensory_others: formData.sensoryOthers,
                vision: formData.vision,
                vision_others: formData.visionOthers,
                hearing: formData.hearing,
                hearing_others: formData.hearingOthers,
                speech: formData.speech,
                speech_others: formData.speechOthers,
                self_caring: toString(formData.selfCaring),
                self_caring_other: formData.selfCaringOther,
                musculoskeletal: toString(formData.musculoskeletal),
                musculoskeletal_other: formData.musculoskeletalOther,
                assisting: toString(formData.assisting),
                assisting_other: formData.assistingOther,
                admission_items: toString(formData.admissionItems),
                cognition: toString(formData.cognition),
                client_level: formData.clientLevel,
                client_level_others: formData.clientLevelOthers,
                room_allocation: formData.roomAllocation,
                inform_medicine: formData.informMedicine,
                guide_family_meetings: formData.guideFamilyMeetings,
                info_consultants: formData.infoConsultants,
                authorize_hair_cutting: formData.authorizeHairCutting,
                isolation_contagious: formData.isolationContagious,
                weight_kg: toNumber(formData.weightKg),
                height_cm: toNumber(formData.heightCm),
                bmi: toNumber(formData.bmi),
                bmi_less_18: formData.bmiLess18,
                bmi_greater_25: formData.bmiGreater25,
                lost_weight_3_months: formData.lostWeight3Months,
                recent_hospitalization: formData.recentHospitalization,
                dietitian_referral_conditions: toString(formData.dietitianReferralConditions),
                refer_dietitian: formData.referDietitian,
                roh_nursing: formData.rohNursing,
                ros_nursing: formData.rosNursing,
                rov_nursing: formData.rovNursing,
                roe_nursing: formData.roeNursing,
                rof_nursing: formData.rofNursing,
                rovu_nursing: formData.rovuNursing,
                suicide_form_nursing: formData.suicideFormNursing2,
                violence_form_nursing: formData.violenceFormNursing2,
                safety_care_plan_nursing: formData.safetyCarePlanNursing2,
                appearance: formData.appearance,
                behavior: formData.behavior,
                speech_mse: formData.speechMse,
                mood_affect: formData.moodAffect,
                thought_process: formData.thoughtProcess,
                thought_content: formData.thoughtContent,
                insight: formData.insight,
                memory: formData.memory,
                self_esteem: formData.selfEsteem,
                hopelessness: formData.hopelessness,
                worthlessness: formData.worthlessness,
                coping: formData.coping,
                marital_status_socio: formData.maritalStatusSocio,
                no_of_children: parseInt(formData.noOfChildren) || null,
                work_situation: formData.workSituation,
                occupation: formData.occupationSocio,
                habit: formData.habit,
                habit_specify: formData.habitSpecify,
                living_situation: formData.livingSituation,
                living_situation_specify: formData.livingSituationSpecify,
            },

            part_five: {
                religion: formData.religion,
                religion_other: formData.religionOther,
                religious_practices: formData.religiousPractices,
                privacy: formData.privacy,
                food: formData.food,
                gender_regalia: formData.genderRegalia,
                other_values: formData.otherValues,
                mouth_tongue: formData.mouthTongue,
                oral_sign_symptom: formData.oralSignSymptom,
                oral_sign_symptom_others: formData.oralSignSymptomOthers,
                ng_gastric: formData.ngGastric,
                ng_gastric_date: formData.ngGastricDate,
                skin_appearance: formData.skinAppearance,
                skin_appearance_other: formData.skinAppearanceOther,
                skin_temperature: formData.skinTemperature,
                skin_contagious: formData.skinContagious,
                skin_location: formData.skinLocation,
                skin_reasons: formData.skinReasons,
                cardio_pulse: toNumber(formData.cardioPulse),
                cardio_sign_symptom: formData.cardioSignSymptom,
                cardio_sign_symptom_other: formData.cardioSignSymptomOther,
                respiration: formData.respiration,
                respiration_other: formData.respirationOther,
                cough: formData.cough,
                cough_other: formData.coughOther,
                bowel_movement: formData.bowelMovement,
                bowel_sign_symptom: formData.bowelSignSymptom,
                urinary_appearance: formData.urinaryAppearance,
                urinary_sign_symptom: formData.urinarySignSymptom,
                date_of_insertion: formData.dateOfInsertion,
                catheter_type: formData.catheterType,
                urinary_others: formData.urinaryOthers,
                obs_gyn: formData.obsGyn,
                menstrual_cycle: formData.menstrualCycle,
                dysmenorrhea: formData.dysmenorrhea,
                skin_changes: formData.skinChanges,
                obs_gyn_others: formData.obsGynOthers,
                nursing_staff: formData.nursingStaff,
                checked_by: formData.checkedBy,
                designation: formData.designation,
                nursing_date: formData.nursingDate,
                nursing_time: formData.nursingTime,
                signature: formData.signature,
                head_nurse: formData.headNurse,
                clinical_nurse_instructor: formData.clinicalNurseInstructor,
                team_leader: formData.teamLeader,
                irp_name: formData.irpName,
                irp_date: formData.irpDate,
                irp_domain: formData.domain,
                irp_rehab_goal: formData.rehabGoal,
                irp_strengths: formData.strengths,
                irp_skills_needed: formData.skillsNeeded,
                irp_client_signature: formData.clientSignature,
                irp_client_date: formData.clientDate,
                irp_practitioner_signature: formData.practitionerSignature,
                irp_practitioner_date: formData.practitionerDate,
                endorsement: formData.endorsement,
            },

            part_six: {
                discharge: formData.discharge,
                discharge_instruction_to: formData.dischargeInstructionTo,
                special_instructions: formData.specialInstructions,
                injections: formData.injections,
                any_red_flag: formData.anyRedFlag,
                risk_assessment_dc: formData.riskAssessmentDC,
                expected_discharge_date: formData.expectedDischargeDate,
                home_care: formData.homeCare,
                home_care_others_specify: formData.homeCareOthersSpecify,
                transportation: formData.transportation,
                psychotherapy: formData.psychotherapy,
                day_rehab: formData.dayRehab,
                day_rehab_days: formData.dayRehabDays,
                long_term_care: formData.longTermCare,
                other_needs: formData.otherNeeds,
                other_needs_specify: formData.otherNeedsSpecify,
                educate_illness: formData.educateIllness ?? "",
                educate_hygiene: formData.educateHygiene ?? "",
                educate_medication: formData.educateMedication ?? "",
                unusual_behavior: formData.unusualBehavior ?? "",
                giving_medicine: formData.givingMedicine ?? "",
                contact_doctor: formData.contactDoctor ?? "",
                client_sharing: formData.clientSharing ?? "",
                engaged_activities: formData.engagedActivities ?? "",
                trigger_relapse: formData.triggerRelapse ?? "",
                sleep_wake: formData.sleepWake ?? "",
                eating_meals: formData.eatingMeals ?? "",
                physical_activity: formData.physicalActivity ?? "",
                coping_strategies: formData.copingStrategies ?? "",
                preventing_relapse: formData.preventingRelapse ?? "",
                socially_connected: formData.sociallyConnected ?? "",
                diet: formData.diet ?? "",
                take_home_medication: formData.takeHomeMedication ?? "",
                compliance_treatment: formData.complianceTreatment ?? "",
                followup_visit: formData.followupVisit ?? "",
                effective_coping: formData.effectiveCoping ?? "",
                hospital_emergency: formData.hospitalEmergency ?? "",
                activity_home: formData.activityHome ?? "",
                stress_management: formData.stressManagement ?? "",
                aggression_management: formData.aggressionManagement ?? "",
                alarming_signs: formData.alarmingSigns ?? "",
                occupational_therapy: formData.occupationalTherapy ?? "",
                social_support: formData.socialSupport ?? "",

                environmental_safety: formData.environmentalSafety ?? "",
                process_giver_role_strain: formData.processGiverRoleStrain ?? "",
                long_term_facilities: formData.longTermFacilities ?? "",
                contact_number: formData.contactNumber ?? "",
                others: formData.others ?? "",
                name_signature: formData.nameSignature ?? "",
                signature_date: formData.signatureDate ?? "",
                signature_time: formData.signatureTime ?? "",

                possible_relapse_sign: formData.possibleRelapseSign ?? "",
                recovery_goals: formData.recoveryGoals ?? "",
                who_to_contact: formData.whoToContact ?? "",
                where_to_go_crises: formData.whereToGoCrises ?? "",
                details_medication: formData.detailsMedication ?? "",
                detail_treatment_plan: formData.detailTreatmentPlan ?? "",
                physical_health_needs: formData.physicalHealthNeeds ?? "",
                date_of_review: formData.dateOfReview ?? "",
                admission_pathway: formData.admissionPathway ?? "",
                risk_assessment_safety_plan: formData.riskAssessmentSafetyPlan ?? "",
                doctor_name: formData.doctorName ?? "",
                guardian_signature: formData.guardianSignature ?? "",
                guardian_name: formData.guardianName ?? "",
                name_of_documents: formData.nameOfDocuments ?? "",
                effective_date: formData.effectiveDate ?? "",
                approved_by: formData.approvedBy ?? "",
                approved_date: formData.approvedDate ?? "",
                next_revision_by: formData.nextRevisionBy ?? "",
                next_revision_date: formData.nextRevisionDate ?? "",
            },

            monthly_pro_notes: (formData.monthlyProgress || []).map(item => ({
                id: item.id, // Include ID so the backend knows which one to update
                integrated_assessment_id: id,
                note_date: item.progressDate,
                date: item.meetingDate,
                progress_note: item.update, // Map 'update' back to 'progress_note'
                family_meeting_minutes: item.familyMeetingMinutes,
                discussion_summary: item.discussionSummary,
            })),

            // Use this ONLY if the backend rejects the array above
            // monthly_pro_notes: formData.monthlyProgress && formData.monthlyProgress.length > 0 ? {
            //     note_date: formData.monthlyProgress[0].progressDate || formData.date,
            //     date: formData.monthlyProgress[0].meetingDate || formData.date,
            //     progress_note: formData.monthlyProgress[0].update,
            //     family_meeting_minutes: formData.monthlyProgress[0].familyMeetingMinutes,
            //     discussion_summary: formData.monthlyProgress[0].discussionSummary
            // } : null,

            // Convert form arrays to API format
            nursing_needs: (formData.nursingProblems || [])
                .filter(item => item.problem)
                .map(item => ({
                    id: item.id, // Include ID for updates
                    nursing_need: item.problem
                })),
            problem_list: (formData.generalProblems || [])
                .filter(item => item.problem)
                .map(item => ({ problem: item.problem })),
            obj_act_plan: (formData.objectives || [])
                .filter(item => item.name)
                .map(item => ({
                    id: item.id, // Include ID for updates
                    objective: item.name || "",
                    action_status: item.obj_status || "Pending",
                    date: item.obj_date || "",
                    steps: (item.actions || []).map(step => ({
                        id: step.id, // Include ID for updates
                        action_step: step.action_name || "",
                        location: step.location || "",
                        frequency: step.frequency || "",
                        duration: step.duration || ""
                    }))
                })),
            cognitive_assessment: formData.cognitive_assessment || {},
            discharge_planning: formData.discharge_planning || {},
        };
    };

    /* =========================
       SUBMIT - OLD CODE REMOVED
    ========================= */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSavingSection("Full Assessment");

        try {
            const payload = buildCompletePayload();
            await dispatch(updateIntegratedAssessment({ id, payload })).unwrap();
            Swal.fire("Updated!", "Assessment updated successfully.", "success");
            // Stay on edit page
        } catch (err) {
            Swal.fire("Error!", getApiErrorMessage(err, "Failed to update the Integrated Assessment."), "error");
        } finally {
            setSavingSection(null);
        }
    };

    if (!formData) return <div className="p-5 text-center">Loading...</div>;

    return (
        <MainLayout>
            <div className="container-xxl container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <IntegratedInitialAssessmentForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                onSectionSave={handleSectionSave}
                                savingSection={savingSection}
                            />
                            <div className="text-end mt-3">
                                <button className="btn btn-primary">
                                    Update Assessment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditIntegratedInitialAssessment;
